/**
 * New Arrivals Quick Add
 * Exact Shopify Horizon native cart drawer flow.
 */

import {
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';

(function () {
  'use strict';

  if (window.__newArrivalsQuickAddLoaded) return;
  window.__newArrivalsQuickAddLoaded = true;

  /**
   * Button loading state.
   */
  function setLoading(button, loading) {
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.disabled = loading;
  }

  /**
   * Toast notification.
   */
  function showToast(message, isError) {
    var oldToast = document.querySelector('.na-toast');

    if (oldToast) {
      oldToast.remove();
    }

    var toast = document.createElement('div');

    toast.className =
      'na-toast' + (isError ? ' na-toast--error' : '');

    toast.textContent = message;

    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    window.setTimeout(function () {
      toast.classList.remove('is-visible');

      window.setTimeout(function () {
        toast.remove();
      }, 300);
    }, 2200);
  }

  /**
   * Return all Horizon cart section IDs.
   */
  function getCartSectionIds() {
    var sectionIds = [];

    document
      .querySelectorAll('cart-items-component[data-section-id]')
      .forEach(function (component) {
        var sectionId = component.dataset.sectionId;

        if (
          sectionId &&
          sectionIds.indexOf(sectionId) === -1
        ) {
          sectionIds.push(sectionId);
        }
      });

    return sectionIds;
  }

  /**
   * Fetch latest cart through Horizon's own cart component.
   */
  async function refreshCart() {
    var cartItemsComponent =
      document.querySelector(
        'cart-drawer-component cart-items-component'
      ) ||
      document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined(
        'cart-items-component'
      );

      if (
        typeof cartItemsComponent.fetchCartData ===
        'function'
      ) {
        return cartItemsComponent.fetchCartData();
      }
    }

    var response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Cart could not be refreshed');
    }

    return response.json();
  }

  /**
   * Additional fallback for visible cart-count elements.
   */
  function updateCartCount(cart) {
    var count = Number(
      cart.item_count ||
      cart.totalQuantity ||
      0
    );

    var selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart-count-bubble span',
      '#cart-icon-bubble span',
      'cart-icon-bubble span',
      '.header-actions__cart-count'
    ];

    selectors.forEach(function (selector) {
      document
        .querySelectorAll(selector)
        .forEach(function (element) {
          element.textContent = count;

          if (count > 0) {
            element.removeAttribute('hidden');
          }
        });
    });
  }

  /**
   * Add variant using Horizon's native CartLinesUpdateEvent.
   */
  async function addToCart(button) {
    var variantId =
      button.getAttribute('data-variant-id');

    if (!variantId || button.disabled) return;

    setLoading(button, true);

    /*
     * Horizon cart-drawer.js listens for this exact event.
     * The drawer opens after this promise resolves.
     */
    var deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: 1
          }
        ],
        promise: deferredEventPromise.promise
      })
    );

    try {
      var formData = new FormData();

      formData.append('id', variantId);
      formData.append('quantity', '1');

      var sectionIds = getCartSectionIds();

      if (sectionIds.length) {
        formData.append(
          'sections',
          sectionIds.join(',')
        );
      }

      var cartAddUrl =
        window.Theme &&
        window.Theme.routes &&
        window.Theme.routes.cart_add_url
          ? window.Theme.routes.cart_add_url
          : '/cart/add.js';

      var response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'text/html'
        },
        body: formData,
        credentials: 'same-origin'
      });

      var result = await response.json();

      if (!response.ok || result.status) {
        var errorMessage =
          result.description ||
          result.message ||
          'Could not add item to cart';

        button.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'INVALID',
            detail: {
              description: result.description,
              errors: result.errors
            }
          })
        );

        var errorCart = await refreshCart();

        deferredEventPromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              errorCart
            ),
          detail: {
            didError: true,
            items: errorCart.items || [],
            source: 'product-form-component',
            sourceId: 'new-arrivals-quick-add',
            itemCount: 1,
            sections: result.sections || {}
          }
        });

        throw new Error(errorMessage);
      }

      /*
       * Horizon drawer HTML and cart data refresh.
       */
      var cart = await refreshCart();

      updateCartCount(cart);

      /*
       * This resolve is what tells cart-drawer.js:
       * request finished, data updated, now open drawer.
       */
      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            cart
          ),
        detail: {
          didError: false,
          items: cart.items || [],
          source: 'product-form-component',
          sourceId: 'new-arrivals-quick-add',
          itemCount: 1,
          sections: result.sections || {}
        }
      });

      showToast('Added to cart', false);

      button.classList.add('is-added');

      window.setTimeout(function () {
        button.classList.remove('is-added');
      }, 1200);
    } catch (error) {
      console.error(
        'New Arrivals quick add error:',
        error
      );

      /*
       * Reject only when promise has not already settled.
       */
      try {
        deferredEventPromise.reject(error);
      } catch (promiseError) {
        // Promise already resolved or rejected.
      }

      button.dispatchEvent(
        new CartErrorEvent({
          error:
            error.message ||
            'Could not add item to cart',
          code: 'SERVICE_UNAVAILABLE'
        })
      );

      showToast(
        error.message ||
        'Could not add item to cart',
        true
      );

      button.classList.add('has-error');

      window.setTimeout(function () {
        button.classList.remove('has-error');
      }, 1500);
    } finally {
      setLoading(button, false);
    }
  }

  /**
   * Delegated click listener.
   */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest(
        '[data-new-arrivals-add]'
      );

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      addToCart(button);
    },
    true
  );
})();